import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api-client';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { LANGUAGES } from '../../i18n/languages';
import { Spinner } from '../../components/ui/spinner';
import { useTranslation } from '../../i18n/I18nContext';
import { readStoredReferralCode, clearStoredReferralCode } from '../../utils/referral-code';

type Step = 'language' | 'sourceLanguage' | 'cefrLevel' | 'currency' | 'affiliateCode';

const CURRENCY_CODES = ['GBP', 'EUR', 'USD'] as const;
const CURRENCY_SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const STEPS: Step[] = ['language', 'sourceLanguage', 'cefrLevel', 'currency', 'affiliateCode'];

// ── Shared step layout ──

interface OnboardingStepProps {
  title: string;
  subtitle: string;
  stepNumber: number;
  totalSteps: number;
  children: React.ReactNode;
  error?: string;
  onBack?: () => void;
  onNext: () => void;
  backLabel: string;
  nextLabel: string;
  nextDisabled?: boolean;
  nextContent?: React.ReactNode;
}

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  title, subtitle, stepNumber, totalSteps,
  children, error, onBack, onNext, backLabel, nextLabel, nextDisabled, nextContent,
}) => (
  <>
    <div className="flex justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          className={`w-2.5 h-2.5 rounded-full ${
            i < stepNumber ? 'bg-success' : 'bg-muted-foreground/30'
          }`}
        />
      ))}
    </div>

    <h1 className="text-2xl font-bold text-foreground mb-2">{title}</h1>
    <p className="text-muted-foreground mb-8">{subtitle}</p>

    {children}

    {error && (
      <div className="bg-destructive text-destructive-foreground rounded-md p-4 mb-4">
        {error}
      </div>
    )}

    <div className="flex gap-3">
      {onBack && (
        <button
          onClick={onBack}
          disabled={nextDisabled}
          className="flex-1 border border-input text-foreground px-6 py-3 rounded-lg text-lg font-semibold hover:bg-muted disabled:opacity-50"
        >
          {backLabel}
        </button>
      )}
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="flex-1 bg-success text-success-foreground px-6 py-3 rounded-lg text-lg font-semibold hover:opacity-90 disabled:opacity-50"
      >
        {nextContent ?? nextLabel}
      </button>
    </div>
  </>
);

// ── Onboarding flow ──

const OnboardingFlow: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('language');
  // Lazy initializer reads the referral code captured on /auth so users
  // who followed a beta invite link see their code pre-applied here
  // instead of having to type it. See utils/referral-code.ts.
  const [affiliateCode, setAffiliateCode] = useState(() => readStoredReferralCode().toUpperCase());
  // `codePreApplied` is true when the code came from the URL via
  // localStorage, so we can render a compact "applied" banner instead
  // of the raw input. A "Change code" link flips it back to input mode.
  const [codePreApplied, setCodePreApplied] = useState(() => readStoredReferralCode().trim().length > 0);
  const [language, setLanguage] = useState('en');
  const [sourceLanguage, setSourceLanguage] = useState('es');
  const [cefrLevel, setCefrLevel] = useState('B1');
  const [currency, setCurrency] = useState<'USD' | 'GBP' | 'EUR'>('GBP');
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { updateSettings } = useSettings();

  const completeOnboarding = async () => {
    setError(undefined);
    setSubmitting(true);

    try {
      const payload: Record<string, any> = {
        displayCurrency: currency,
        uiLanguage: language,
        sourceLanguage,
        cefrLevel,
      };
      if (affiliateCode.trim()) {
        payload.affiliateCode = affiliateCode.trim();
      }
      await apiClient.post('/accounts/onboarding/complete', payload);
      await updateSettings({ language, sourceLanguage, cefrLevel, audioVoice: 'female', audioSpeed: 1.0 });
      // Clear the stored referral code once we've successfully applied
      // it — stops a returning user from seeing a stale banner on
      // re-signup if they come back through the same browser.
      clearStoredReferralCode();
      await refreshUser();

      navigate('/articles', { replace: true });
    } catch (err) {
      console.error('Onboarding failed:', err);
      setError(err instanceof Error ? err.message : t('onboarding.setup_failed'));
      setSubmitting(false);
    }
  };

  const stepNumber = STEPS.indexOf(step) + 1;
  const totalSteps = STEPS.length;
  const stepProps = { stepNumber, totalSteps };

  const goToStep = (next: Step) => {
    setStep(next);
    const nextStepNumber = STEPS.indexOf(next) + 1;
    // Fire-and-forget — don't block the UI on analytics
    apiClient.post('/accounts/onboarding/progress', { step: nextStepNumber }).catch(() => {});
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <nav className="shrink-0 border-b border-border bg-background">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center h-14">
            <span className="flex items-center gap-2 text-lg font-bold text-foreground">
              <img src="/icon-192.png" alt="" className="h-8 w-8 rounded-lg" />
              {import.meta.env.VITE_BRAND_NAME}
            </span>
          </div>
        </div>
      </nav>
      <div className="flex-1 flex justify-center px-4 pt-16">
      <div className="w-full max-w-md">

        {step === 'language' && (
          <OnboardingStep
            {...stepProps}
            title={t('onboarding.language_title')}
            subtitle={t('onboarding.language_subtitle')}
            onNext={() => goToStep('sourceLanguage')}
            backLabel={t('onboarding.back')}
            nextLabel={t('onboarding.next')}
          >
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('onboarding.language_label')}
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground mb-8"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </option>
              ))}
            </select>
          </OnboardingStep>
        )}

        {step === 'sourceLanguage' && (
          <OnboardingStep
            {...stepProps}
            title={t('onboarding.source_language_title')}
            subtitle={t('onboarding.source_language_subtitle')}
            onBack={() => goToStep('language')}
            backLabel={t('onboarding.back')}
            onNext={() => goToStep('cefrLevel')}
            nextLabel={t('onboarding.next')}
          >
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('onboarding.source_language_label')}
            </label>
            <select
              value={sourceLanguage}
              onChange={(e) => setSourceLanguage(e.target.value)}
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground mb-8"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </option>
              ))}
            </select>
          </OnboardingStep>
        )}

        {step === 'cefrLevel' && (
          <OnboardingStep
            {...stepProps}
            title={t('onboarding.cefr_title')}
            subtitle={t('onboarding.cefr_subtitle')}
            onBack={() => goToStep('sourceLanguage')}
            backLabel={t('onboarding.back')}
            onNext={() => goToStep('currency')}
            nextLabel={t('onboarding.next')}
          >
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('onboarding.cefr_label')}
            </label>
            <div className="flex flex-wrap gap-2 mb-8">
              {CEFR_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setCefrLevel(level)}
                  className={`px-4 py-2.5 rounded-lg text-lg font-medium transition-colors ${
                    cefrLevel === level
                      ? 'bg-success text-success-foreground'
                      : 'border border-input text-foreground hover:bg-muted'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </OnboardingStep>
        )}

        {step === 'currency' && (
          <OnboardingStep
            {...stepProps}
            title={t('onboarding.currency_title')}
            subtitle={t('onboarding.currency_subtitle')}
            onBack={() => goToStep('cefrLevel')}
            backLabel={t('onboarding.back')}
            onNext={() => goToStep('affiliateCode')}
            nextLabel={t('onboarding.next')}
          >
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('onboarding.currency_label')}
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'USD' | 'GBP' | 'EUR')}
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground mb-8"
            >
              {CURRENCY_CODES.map((code) => (
                <option key={code} value={code}>
                  {CURRENCY_SYMBOLS[code]} {t(`onboarding.currency_${code.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </OnboardingStep>
        )}

        {step === 'affiliateCode' && (
          <OnboardingStep
            {...stepProps}
            title={t('onboarding.affiliate_title')}
            subtitle={t('onboarding.affiliate_subtitle')}
            onBack={() => goToStep('currency')}
            backLabel={t('onboarding.back')}
            onNext={completeOnboarding}
            nextLabel={t('onboarding.finish')}
            nextDisabled={submitting}
            error={error}
            nextContent={submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size="sm" className="border-primary-foreground" />
                {t('onboarding.setting_up')}
              </span>
            ) : undefined}
          >
            {codePreApplied ? (
              <div className="mb-8 border border-success rounded-md bg-success/10 p-4">
                <div className="flex items-center gap-2 text-success font-semibold text-sm">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Referral code applied</span>
                </div>
                <div className="mt-1 font-mono text-base text-foreground">{affiliateCode}</div>
                <button
                  type="button"
                  onClick={() => {
                    setCodePreApplied(false);
                    setAffiliateCode('');
                  }}
                  className="mt-2 text-xs text-muted-foreground underline hover:text-foreground"
                >
                  Change code
                </button>
              </div>
            ) : (
              <>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('onboarding.affiliate_label')}
                </label>
                <input
                  type="text"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())}
                  placeholder="AFF-XXXXX"
                  className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground mb-8 uppercase"
                />
              </>
            )}
          </OnboardingStep>
        )}

      </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
